import { useCallback, useEffect, useState } from "react"

import { extractConsolidatedDeliveryTerms } from "./utils/common"

import { type PricingPackage } from "@/graphql/codegen/graphql"

export interface ConsolidatedDeliveryTerms {
  freightTermsConsolidated: string | null
  incotermsConsolidated: string | null
  incotermsLocationIdConsolidated: string | null
}

/**
 * Hook to adjust delivery terms based on pricing packages.
 *
 * - Accepts Pricing Packages explicitly (no store/context dependency)
 * - Return `getConsolidatedDeliveryTerms` method to consolidate delivery terms across packages
 * - Compare each of 3 delivery terms definitions separately
 *
 */
const useDeliveryTermsPerRequestedDate = ({
  onSuccess,
  pricingPackages,
  pricingFetching = false,
}: {
  onSuccess?: (deliveryTerms: ConsolidatedDeliveryTerms) => void
  pricingPackages: PricingPackage[]
  pricingFetching?: boolean
}): {
  getConsolidatedDeliveryTerms: () => ConsolidatedDeliveryTerms
  isPricingFetching: boolean
  shouldRecalculateDeliveryTerms: boolean
  handleRecalculateDeliveryTerms: () => void
} => {
  const [shouldRecalculateDeliveryTerms, setShouldRecalculateDeliveryTerms] = useState<boolean>(false)

  const handleRecalculateDeliveryTerms = useCallback(() => {
    setShouldRecalculateDeliveryTerms(true)
  }, [])

  const getConsolidatedDeliveryTerms = useCallback(() => {
    return extractConsolidatedDeliveryTerms(pricingPackages)
  }, [pricingPackages])

  // If shouldRecalculate is true, recalculate delivery terms after all pricing data is fetched
  useEffect(() => {
    if (shouldRecalculateDeliveryTerms && !pricingFetching && pricingPackages.length > 0 && onSuccess) {
      onSuccess(getConsolidatedDeliveryTerms())
      setShouldRecalculateDeliveryTerms(false) // Reset the flag after recalculation
    }
  }, [getConsolidatedDeliveryTerms, shouldRecalculateDeliveryTerms, onSuccess, pricingFetching, pricingPackages])

  return {
    getConsolidatedDeliveryTerms,
    isPricingFetching: pricingFetching,
    handleRecalculateDeliveryTerms,
    shouldRecalculateDeliveryTerms,
  }
}

export default useDeliveryTermsPerRequestedDate
